// src/App.js
import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import CookieConsent from './components/common/CookieConsent';
import ScrollHandler from './components/common/ScrollHandler';
import ErrorBoundary from './components/common/ErrorBoundary';
import AdminRoute from './components/common/AdminRoute';
import PwaStatusPrompt from './components/common/PwaStatusPrompt';
import LoadingScreen from './components/common/LoadingScreen';
import { logger } from './utils/logger';
import { trackPageView } from './services/analyticsService';
import { baseURL } from './services/api';
import { getOfflineAudioManifest } from './services/offlineAudioService';

const HomePage = lazy(() => import('./pages/HomePage'));
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const MusicPlayer = lazy(() => import('./pages/MusicPlayer'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const ResetRequest = lazy(() => import('./pages/ResetRequest'));
const SubscriptionPlans = lazy(() => import('./pages/SubscriptionPlans'));
const Register = lazy(() => import('./pages/Register'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const VerifyEmailHandler = lazy(() => import('./pages/VerifyEmailHandler'));
const Payment = lazy(() => import('./pages/Payment'));
const StudentOnboarding = lazy(() => import('./pages/StudentOnboarding'));
const BrowseMusic = lazy(() => import('./pages/BrowseMusic'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const FAQ = lazy(() => import('./pages/FAQ'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const PlaylistsPage = lazy(() => import('./pages/PlaylistsPage'));
const ProgressPage = lazy(() => import('./pages/ProgressPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ChallengesPage = lazy(() => import('./pages/ChallengesPage'));
const ContentRequestsPage = lazy(() => import('./pages/ContentRequestsPage'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const OfflineLibrary = lazy(() => import('./pages/OfflineLibrary'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const AdminMusicUpload = lazy(() => import('./pages/AdminMusicUpload'));
const AdminCurriculumSetup = lazy(() => import('./pages/AdminCurriculumSetup'));
const AdminRequests = lazy(() => import('./pages/AdminRequests'));

const AppRouteFallback = () => (
  <LoadingScreen
    title="Loading EduVibz"
    subtitle="Tuning the next screen for your study session..."
    compact
  />
);

const hasOfflineSong = (songId) => {
  if (!songId) return false;
  return Boolean(getOfflineAudioManifest()[songId]);
};

const getOfflineSongIdFromPath = (path = '') => (
  path.match(/^\/music-player\/song\/([^/]+)/)?.[1]
);

const isOfflineSongPlayerPath = (path = '') => (
  hasOfflineSong(getOfflineSongIdFromPath(path))
);

const getApiHealthUrl = () => baseURL.replace(/\/api\/?$/, '/health');

const canReachApiHealth = async () => {
  if (typeof fetch !== 'function') return false;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 2500);

  try {
    const response = await fetch(getApiHealthUrl(), {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal
    });
    return response.ok;
  } catch (error) {
    logger.debug('API health check failed during offline redirect check', { error });
    return false;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const ProtectedRoute = ({ children, allowOfflineSong = false }) => {
  const { isLoggedIn, loading, sessionExpired } = useAuth();
  const location = useLocation();
  const offlineSongId = getOfflineSongIdFromPath(location.pathname);

  if (allowOfflineSong && hasOfflineSong(offlineSongId)) {
    return <ErrorBoundary>{children}</ErrorBoundary>;
  }

  if (loading) {
    return (
      <LoadingScreen
        title="Checking your session"
        subtitle="Making sure your playlists, progress, and study music are ready."
        compact
      />
    );
  }

  if (!isLoggedIn) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname, sessionExpired }}
      />
    );
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
};

const ContentWrapper = ({ children }) => {
  const location = useLocation();
  const { isLoggedIn } = useAuth();
  const excludeFooterPaths = ['/music-player', '/music-player/chapter', '/music-player/song', '/music-player/playlist'];

  // Track page views
  useEffect(() => {
    trackPageView(location.pathname + location.search, { acquisitionOnly: true }).catch((error) => {
      logger.warn('Page view tracking failed', { error });
    });
    logger.info('Page view tracked', { path: location.pathname });
  }, [location]);

  const shouldExcludeFooter = 
    isLoggedIn || // Hide footer if user is logged in
    excludeFooterPaths.some(path => location.pathname.startsWith(path));

  return (
    <div className="flex flex-col min-h-screen">
      <ErrorBoundary>
        <Header />
      </ErrorBoundary>
      <main className="flex-grow">
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>
      {!shouldExcludeFooter && (
        <ErrorBoundary>
          <Footer />
        </ErrorBoundary>
      )}
    </div>
  );
};

const OfflineModeRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const redirectIfOffline = async () => {
      if (location.pathname === '/offline') return;
      if (isOfflineSongPlayerPath(location.pathname)) return;
      const networkOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

      if (networkOffline) {
        const apiReachable = await canReachApiHealth();
        if (!cancelled && !apiReachable) {
          navigate('/offline', { replace: true, state: { from: location.pathname } });
        }
      }
    };

    redirectIfOffline();
    window.addEventListener('offline', redirectIfOffline);
    return () => {
      cancelled = true;
      window.removeEventListener('offline', redirectIfOffline);
    };
  }, [location.pathname, navigate]);

  return null;
};

const App = () => {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <AuthProvider>
          <Router>
            <ScrollHandler>
              <OfflineModeRedirect />
              <ContentWrapper>
                <Suspense fallback={<AppRouteFallback />}>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="/music-player/chapter/:chapterId" element={<ProtectedRoute><MusicPlayer /></ProtectedRoute>} />
                    <Route path="/music-player/song/:songId" element={<ProtectedRoute allowOfflineSong><MusicPlayer /></ProtectedRoute>} />
                    <Route path="/music-player/playlist/:playlistId" element={<ProtectedRoute><MusicPlayer /></ProtectedRoute>} />
                    <Route path="/music-player" element={<ProtectedRoute><MusicPlayer /></ProtectedRoute>} />
                    <Route path="/changepassword" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
                    <Route path="/resetrequest" element={<ResetRequest />} />
                    <Route path="/pricing" element={<SubscriptionPlans />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/verify-email-handler" element={<VerifyEmailHandler />} />
                    <Route path="/payment" element={<ProtectedRoute><Payment /></ProtectedRoute>} />
                    <Route path="/student-onboarding" element={<ProtectedRoute><StudentOnboarding /></ProtectedRoute>} />
                    <Route path="/browse" element={<ProtectedRoute><BrowseMusic /></ProtectedRoute>} />
                    <Route path="/about" element={<AboutUs />} />
                    <Route path="/how-it-works" element={<HowItWorks />} />
                    <Route path="/contact" element={<ContactUs />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                    <Route path="/playlists" element={<ProtectedRoute><PlaylistsPage /></ProtectedRoute>} />
                    <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
                    <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                    <Route path="/challenges" element={<ProtectedRoute><ChallengesPage /></ProtectedRoute>} />
                    <Route path="/requests" element={<ContentRequestsPage />} />
                    <Route path="/cookie-policy" element={<CookiePolicy />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/offline" element={<OfflineLibrary />} />
                    <Route element={<AdminRoute />}>
                      <Route path="/admin" element={<Navigate to="/admin/analytics" replace />} />
                      <Route path="/admin/analytics" element={<AdminAnalytics />} />
                      <Route path="/admin/curriculum" element={<AdminCurriculumSetup />} />
                      <Route path="/admin/music-upload" element={<AdminMusicUpload />} />
                      <Route path="/admin/requests" element={<AdminRequests />} />
                    </Route>
                  </Routes>
                </Suspense>
                </ContentWrapper>
              </ScrollHandler>
              <ErrorBoundary>
                <CookieConsent />
              </ErrorBoundary>
              <ErrorBoundary>
                <PwaStatusPrompt />
              </ErrorBoundary>
            </Router>
        </AuthProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
};

export default App;
